import React from "react";

import _ from "lodash";

import PIIFields from "../PIIFields";
import ToolTipComponent from "../ToolTipComponent";

import * as DetailList from "~/components/DetailList";
import { Box, Section, Padding, Separator } from "~/components/Elements";
import SkeletonDetailList from "~/components/SkeletonDetailList";
import getCountry from "~/data/getCountry";
import { TransactionWrap, Direction } from "~/hooks/use-transaction";

type TransactionPartProps = {
  transaction?: TransactionWrap | null;
  direction?: Direction | undefined;
  requiredFields: string[];
  warnings?: string[];
  isEncryptionEnabled: boolean;
  refreshButtonForSanctions?: boolean;
};

export type DetailListItem = {
  label?: string;
  value?: string;
  isEncrypted?: boolean;
  ref?: string;
  mobile?: number;
  tablet?: number;
  desktop?: number;
  warning?: string;
};

export default function TransactionBeneficiary({
  transaction,
  direction,
  requiredFields,
  warnings,
  isEncryptionEnabled,
  refreshButtonForSanctions,
  ...props
}: TransactionPartProps) {
  const personalInfoFields = [
    ...(transaction?.beneficiaryNatural
      ? [
          {
            label: "First Name",
            value: transaction?.beneficiaryFirstName,
            desktop: 3,
            warning: "beneficiaryNameMissing",
          },
        ]
      : []),

    {
      label: transaction?.beneficiaryNatural ? "Last Name" : "Name",
      value: transaction?.beneficiaryLastName,
      desktop: 3,
      warning: "beneficiaryNameMissing",
    },
    {
      label: "Account Number",
      value: _.get(transaction, "ivms101.beneficiary.accountNumber[0]"),
      desktop: 3,
    },
    {
      label: "Sanction List",
      value: direction === "outgoing" && (
        <ToolTipComponent
          transaction={transaction}
          direction={direction}
          refreshButtonForSanctions={refreshButtonForSanctions}
        />
      ),
      isEncrypted: false,
      desktop: 3,
    },
    {
      label: "Customer reference",
      value: transaction?.beneficiaryRef,
      desktop: 6,
    }, // TODO: desktop=6?
    // { label: "VAAN Number", value: "826518722" },
  ] as DetailListItem[];
  const addressFields = transaction?.beneficiaryAddressLine1
    ? ([
        {
          label: "Address 1",
          ref: "beneficiaryAddressLine1",
          warning: "beneficiaryGeographicAddressMissing",
        },
        {
          label: "Address 2",
          ref: "beneficiaryAddressLine2",
          warning: "beneficiaryGeographicAddressMissing",
        },
        { label: "City", ref: "beneficiaryAddressCity", warning: "beneficiaryGeographicAddressMissing" },
        {
          label: "Country",
          value: getCountry(transaction.beneficiaryAddressCountry),
          warning: "beneficiaryGeographicAddressMissing",
        },
      ] as DetailListItem[])
    : ([
        {
          label: "Street",
          ref: "beneficiaryAddressStreet",
          warning: "beneficiaryGeographicAddressMissing",
        },
        {
          label: "Building number",
          ref: "beneficiaryAddressNumber",
          warning: "beneficiaryGeographicAddressMissing",
        },
        {
          label: "Suite / Unit Number",
          ref: "beneficiaryAddressUnit",
          warning: "beneficiaryGeographicAddressMissing",
        },
        { label: "City", ref: "beneficiaryAddressCity", warning: "beneficiaryGeographicAddressMissing" },
        {
          label: "Province / State",
          ref: "beneficiaryAddressState",
          warning: "beneficiaryGeographicAddressMissing",
        },
        {
          label: "Postal Code",
          ref: "beneficiaryAddressPostCode",
          warning: "beneficiaryGeographicAddressMissing",
        },
        {
          label: "Country",
          value: getCountry(transaction?.beneficiaryAddressCountry),
          warning: "beneficiaryGeographicAddressMissing",
        },
      ] as DetailListItem[]);

  return (
    <Box as={Section} {...props}>
      <Padding sized="medium">
        <DetailList.Header>
          <DetailList.Item mobile={12}>
            <DetailList.Title as="h2">Beneficiary</DetailList.Title>
          </DetailList.Item>
        </DetailList.Header>

        <DetailList.Header>
          <DetailList.Item mobile={12}>
            <DetailList.Title as="h3" size="6">
              Personal information
            </DetailList.Title>
          </DetailList.Item>
        </DetailList.Header>

        {transaction ? (
          <PIIFields
            fields={personalInfoFields}
            transaction={transaction}
            requiredFields={requiredFields}
            warnings={warnings}
            isEncryptionEnabled={isEncryptionEnabled}
            direction={direction}
          />
        ) : (
          <SkeletonDetailList count={1} />
        )}

        <Separator />

        <DetailList.Header>
          <DetailList.Item mobile={12}>
            <DetailList.Title as="h3" size="6">
              Address
            </DetailList.Title>
          </DetailList.Item>
        </DetailList.Header>

        {transaction ? (
          <PIIFields
            fields={addressFields}
            transaction={transaction}
            requiredFields={requiredFields}
            warnings={warnings}
            isEncryptionEnabled={isEncryptionEnabled}
            direction={direction}
          />
        ) : (
          <SkeletonDetailList count={4} />
        )}
      </Padding>
    </Box>
  );
}
